import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  IconButton,
  Chip,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import moment from "moment";
import DeleteConfirmationDialog from "app/components/Dialogs/DeleteConfirmationDialog";
import InfiniteScrollTable from "app/components/Tables/InfiniteScrollTable";
import {
  getTenant,
  upsertTenantInfo,
  deleteTenant,
} from "app/services/tenant/orgTenant.service";
import TenantForm from "../components/AddTenantForm";
import { LoadingButton } from "@mui/lab";
import { useMutation } from "react-query";

const TenantListPage = () => {
  const [state, setState] = useState({
    tableData: [],
    openForm: false,
    openView: false,
    openDelete: false,
    selectedTenant: null,
    isEdit: false,
    domainDeleteDialog: false,
    selectedDomain: null,
    selectedTenantForDomain: null,
    addDomainDialog: false,
    selectedTenantForAddDomain: null,
    newDomainValue: "",
  });
  const [enabled, setEnabled] = useState(true);

  const handleAdd = () => {
    setState((prev) => ({
      ...prev,
      selectedTenant: null,
      isEdit: false,
      openForm: true,
    }));
  };

  const handleEdit = (tenant) => {
    setState((prev) => ({
      ...prev,
      selectedTenant: tenant,
      isEdit: true,
      openForm: true,
    }));
  };

  const handleDelete = (tenant) => {
    setState((prev) => ({
      ...prev,
      selectedTenant: tenant,
      openDelete: true,
    }));
  };

  const handleDomainDelete = (tenant, domain) => {
    setState((prev) => ({
      ...prev,
      domainDeleteDialog: true,
      selectedDomain: domain,
      selectedTenantForDomain: tenant,
    }));
  };

  const handleDomainAdd = (tenant) => {
    setState((prev) => ({
      ...prev,
      addDomainDialog: true,
      selectedTenantForAddDomain: tenant,
      newDomainValue: "",
    }));
  };

  const { mutate: upsertTenantMutate, isLoading: upsertTenantLoading } =
    useMutation(upsertTenantInfo, {
      onSuccess: ({ data }) => {
        setEnabled((prev) => !prev); // Refresh the table by toggling state
        toast.success(
          state.domainDeleteDialog
            ? "Domain removed successfully"
            : "Domain added successfully"
        );
        setState((prev) => ({
          ...prev,
          domainDeleteDialog: false,
          addDomainDialog: false,
          selectedDomain: null,
          selectedTenantForDomain: null,
          selectedTenantForAddDomain: null,
          newDomainValue: "",
        }));
      },
      onError: ({ response: { data } }) => {
        toast.error(data?.message || "Failed to update domain");
      },
    });

  const { mutate: deleteTenantMutate, isLoading: deleteTenantLoading } =
    useMutation(deleteTenant, {
      onSuccess: ({ data }) => {
        setEnabled((prev) => !prev); // Refresh the table by toggling state
        toast.success("Tenant deleted successfully");
        setState((prev) => ({
          ...prev,
          openDelete: false,
          selectedTenant: null,
        }));
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to delete tenant"
        );
      },
    });

  const handleDomainDeleteConfirm = () => {
    const { selectedTenantForDomain, selectedDomain } = state;

    if (!selectedTenantForDomain || !selectedDomain) {
      toast.error("Invalid selection");
      return;
    }

    // Filter out the selected domain
    const updatedDomains = selectedTenantForDomain.allowedDomains.filter(
      (domain) => domain !== selectedDomain
    );

    // Prepare payload with updated domains
    const payload = {
      _id: selectedTenantForDomain._id,
      tenantName: selectedTenantForDomain.tenantName,
      tenantid: selectedTenantForDomain.tenantid,
      schoolDocId: selectedTenantForDomain.schoolDocId,
      environment: selectedTenantForDomain.environment,
      erpLoginUrl: selectedTenantForDomain.erpLoginUrl,
      schoolWebUrl: selectedTenantForDomain.schoolWebUrl,
      allowedDomains: updatedDomains,
    };

    upsertTenantMutate(payload);
  };

  const handleDomainAddConfirm = () => {
    const { selectedTenantForAddDomain, newDomainValue } = state;

    if (!selectedTenantForAddDomain || !newDomainValue.trim()) {
      toast.error("Please enter a valid domain");
      return;
    }

    // Check if domain already exists
    if (
      selectedTenantForAddDomain.allowedDomains.includes(newDomainValue.trim())
    ) {
      toast.error("Domain already exists");
      return;
    }

    // Add the new domain
    const updatedDomains = [
      ...selectedTenantForAddDomain.allowedDomains,
      newDomainValue.trim(),
    ];

    // Prepare payload with updated domains
    const payload = {
      _id: selectedTenantForAddDomain._id,
      tenantName: selectedTenantForAddDomain.tenantName,
      tenantid: selectedTenantForAddDomain.tenantid,
      schoolDocId: selectedTenantForAddDomain.schoolDocId,
      environment: selectedTenantForAddDomain.environment,
      erpLoginUrl: selectedTenantForAddDomain.erpLoginUrl,
      schoolWebUrl: selectedTenantForAddDomain.schoolWebUrl,
      allowedDomains: updatedDomains,
    };

    upsertTenantMutate(payload);
  };


  const handleDeleteConfirm = () => {
    if (state.selectedTenant?._id) {
      deleteTenantMutate(state.selectedTenant._id);
    } else {
      toast.error("Invalid tenant selected");      
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Sr.No",
        Cell: ({ cell }) => (
          <Typography>{parseInt(cell.row.id) + 1}</Typography>
        ),
        maxSize: 20,
      },
      {
        header: "Tenant ID",
        accessorKey: "tenantid",
        maxSize: 40,
        Cell: ({ cell }) => (
          <Typography fontWeight="medium">{cell.getValue()}</Typography>
        ),
      },
      {
        header: "Tenant Name",
        accessorKey: "tenantName",
        maxSize: 60,
        Cell: ({ cell }) => <Typography>{cell.getValue()}</Typography>,
      },
      {
        header: "School Doc ID",
        accessorKey: "schoolDocId",
        maxSize: 60,
        Cell: ({ cell }) => <Typography>{cell.getValue()}</Typography>,
      },
      {
        header: "Web URL",
        accessorKey: "schoolWebUrl",
        maxSize: 60,
        Cell: ({ cell }) => <Typography>{cell.getValue()}</Typography>,
      },
      {
        header: "ERP Login Url",
        accessorKey: "erpLoginUrl",
        maxSize: 60,
        Cell: ({ cell }) => <Typography>{cell.getValue()}</Typography>,
      },
      {
        header: "Env",
        accessorKey: "environment",
        maxSize: 20,
        Cell: ({ cell }) => (
          <Typography textTransform="uppercase">{cell.getValue()}</Typography>
        ),
      },
      {
        header: "Allowed Domains",
        accessorKey: "allowedDomains",
        maxSize: 80,
        Cell: ({ cell }) => {
          const domains = cell.getValue() || [];
          const tenant = cell.row.original;
          return (
            <Box sx={{ maxWidth: 400 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                {domains.map((domain, index) => (
                  <Chip
                    key={index}
                    label={domain}
                    size="small"
                    variant="outlined"
                    color="primary"
                    onDelete={() => handleDomainDelete(tenant, domain)}
                    sx={{
                      fontSize: "10px",
                      height: "auto",
                      minHeight: "24px",
                      maxWidth: "350px",
                      alignSelf: "flex-start",
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        wordBreak: "break-all",
                        lineHeight: 1.2,
                        padding: "4px 8px",
                        display: "block",
                      },
                      "& .MuiChip-deleteIcon": {
                        fontSize: "14px",
                        alignSelf: "flex-start",
                        marginTop: "2px",
                      },
                    }}
                  />
                ))}
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Add />}
                onClick={() => handleDomainAdd(tenant)}
                sx={{
                  fontSize: "10px",
                  height: "22px",
                  textTransform: "none",
                  borderColor: "#E0E7F1",
                  borderRadius: "14px",
                  color: "#6B7280",
                  "&:hover": {
                    borderColor: "#1976d2",
                    color: "#1976d2",
                  },
                }}
              >
                Add Domain
              </Button>
            </Box>
          );
        },
      },

      {
        header: "Created On",
        accessorKey: "createdOn",
        maxSize: 50,
        Cell: ({ cell }) => (
          <Typography>
            {moment(cell.getValue()).format("DD/MM/YYYY")}
          </Typography>
        ),
      },

      {
        header: "Actions",
        maxSize: 50,
        Cell: ({ cell }) => (
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={() => handleEdit(cell.row.original)}
              color="primary"
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDelete(cell.row.original)}
              color="error"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    []
  );

  return (
    <>
      <Helmet>
        <title>Tenant Management | EkalSutra</title>
      </Helmet>
      <Container maxWidth="xl" >
        <Box >
          <Box sx={{display:"flex", justifyContent:"space-between" }} mb={1}>
            {/* Title  */}
            <Box
            >
              <Typography variant="h5" fontWeight="bold" color="primary">
                Tenant Management
              </Typography>
            </Box>
            {/* Add tenant action */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Add />}
                onClick={handleAdd}
                sx={{ borderRadius: 2, mr: 2 }}
              >
                Add Tenant
              </Button>
              {/* <TenantFilterDialog onFilterApply={setFilterParams} /> */}
            </Box>
          </Box>

          <InfiniteScrollTable
            columns={columns}
            maxHeight="60vh"
            apiUrl={getTenant}
            setState={setState}
            enabled={enabled}
            setEnabled={setEnabled}
            filterRefresh={false}
          />
        </Box>

        {/* Add/Edit Tenant Dialog */}
        <TenantForm
          open={state.openForm}
          edit={state.isEdit}
          editData={state.selectedTenant}
          onClose={() =>
            setState((prev) => ({
              ...prev,
              openForm: false,
              selectedTenant: null,
              isEdit: false,
            }))
          }
          refetch={() => setEnabled((prev) => !prev)}
        />

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          open={state.openDelete}
          onClose={() => setState((prev) => ({ ...prev, openDelete: false }))}
          handleDelete={handleDeleteConfirm}
          loading={deleteTenantLoading}
          tittle={`delete tenant "${state.selectedTenant?.tenantName}"`}
        />

        {/* Domain Delete Confirmation Dialog */}
        <DeleteConfirmationDialog
          open={state.domainDeleteDialog}
          onClose={() =>
            setState((prev) => ({ ...prev, domainDeleteDialog: false }))
          }
          handleDelete={handleDomainDeleteConfirm}
          loading={upsertTenantLoading}
          tittle={"remove domain"}
        />

        {/* Add Domain Dialog */}
        <Dialog
          open={state.addDomainDialog}
          onClose={() =>
            setState((prev) => ({ ...prev, addDomainDialog: false }))
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add New Domain</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Add a new domain to tenant "
              {state.selectedTenantForAddDomain?.tenantName}".
            </DialogContentText>
            <TextField
              autoFocus
              fullWidth
              label="Domain"
              placeholder="Enter domain (e.g., school.example.com)"
              value={state.newDomainValue}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  newDomainValue: e.target.value,
                }))
              }
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleDomainAddConfirm();
                }
              }}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2.5, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() =>
                setState((prev) => ({ ...prev, addDomainDialog: false }))
              }
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={upsertTenantLoading}
              onClick={handleDomainAddConfirm}
              disabled={!state.newDomainValue.trim()}
              sx={{ textTransform: "none" }}
            >
              Add Domain
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default TenantListPage;
